import { ComputedDatum, ResponsivePie } from '@nivo/pie';
import * as React from 'react';
import { Config } from '../config';
import { useLanguage } from '../locale';

export type StressPieArcData = ComputedDatum<StressPieData>;

type StressPieData = {
  id: string;
  label: string;
  value: number;
};

const stressPieColors = [
  Config.dangerColor, // red for stressed
  Config.primaryColor, // bright green for not stressed
];

const pieMargins = { bottom: 80, top: 25, left: 80, right: 80 };

type StressPieChartProps = {
  stressedCount: number;
  notStressedCount: number;
  containerClassName?: string;
  onSliceClick?: (data: StressPieArcData) => void;
};

const StressPieChart: React.FC<StressPieChartProps> = ({
  stressedCount,
  notStressedCount,
  containerClassName = '',
  onSliceClick,
}) => {
  const [t] = useLanguage();

  const pieData = React.useMemo((): StressPieData[] => {
    const data = [
      {
        id: 'stressed',
        label: t('dashboard.analytics.stressed'),
        value: stressedCount,
      },
      {
        id: 'notStressed',
        label: t('dashboard.analytics.notStressed'),
        value: notStressedCount,
      },
    ];

    return data.filter((item) => item.value > 0); // Only show slices with data
  }, [stressedCount, notStressedCount, t]);

  const totalCount = stressedCount + notStressedCount;

  if (totalCount === 0) {
    return (
      <div
        className={`h-80 py-4 flex items-center justify-center ${containerClassName}`}
      >
        <span className="text-gray-500">{t('shared.noData')}</span>
      </div>
    );
  }

  return (
    <div className={`h-80 py-4 ${containerClassName}`}>
      <ResponsivePie
        data={pieData}
        margin={pieMargins}
        onClick={onSliceClick}
        activeOuterRadiusOffset={2}
        innerRadius={0.6}
        padAngle={0.5}
        cornerRadius={5}
        enableArcLinkLabels={false}
        enableArcLabels={false}
        arcLinkLabel={(e) => {
          return e.id + ' (' + e.value + ')';
        }}
        colors={stressPieColors}
        legends={[
          {
            anchor: 'bottom',
            direction: 'row',
            justify: false,
            translateX: 0,
            translateY: 56,
            itemsSpacing: 0,
            itemWidth: 100,
            itemHeight: 18,
            itemTextColor: '#999',
            itemDirection: 'left-to-right',
            itemOpacity: 1,
            symbolSize: 18,
            symbolShape: 'circle',
            effects: [
              {
                on: 'hover',
                style: {
                  itemTextColor: '#000',
                },
              },
            ],
          },
        ]}
      />
    </div>
  );
};

export default StressPieChart;
